import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';

/** Compact line icons for the nav - inherit currentColor. */
const I: Record<string, ReactNode> = {
  overview: <path d="M4 13h7V4H4v9Zm9 7h7v-9h-7v9ZM4 20h7v-5H4v5ZM13 4v5h7V4h-7Z" />,
  verify: <path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6l7-3Zm-1 11l5-5-1.4-1.4L11 11.2 9.4 9.6 8 11l3 3Z" />,
  users: <path d="M16 11a4 4 0 10-4-4 4 4 0 004 4Zm-8 1a3 3 0 10-3-3 3 3 0 003 3Zm8 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4Zm-8 .5c-2 .2-5 1.2-5 3.5V20h3v-2c0-1.4.6-2.6 2-3.5Z" />,
  appeals: <path d="M12 3l9 4-9 4-9-4 9-4Zm-7 7l7 3 7-3v4l-7 3-7-3v-4Z" />,
  listings: <path d="M20.6 11.4l-8-8A2 2 0 0011.2 3H5a2 2 0 00-2 2v6.2a2 2 0 00.6 1.4l8 8a2 2 0 002.8 0l6.2-6.2a2 2 0 000-3ZM7.5 9A1.5 1.5 0 119 7.5 1.5 1.5 0 017.5 9Z" />,
  orders: <path d="M3 4h2l2.4 12.3a1 1 0 001 .7h9.9a1 1 0 001-.8L21 8H7M9 20a1 1 0 100 2 1 1 0 000-2Zm8 0a1 1 0 100 2 1 1 0 000-2Z" />,
  escrow: <path d="M12 2l9 4v6c0 5-3.5 8.5-9 10-5.5-1.5-9-5-9-10V6l9-4Zm-1 6v2H9v2h2v4h2v-4h2v-2h-2V8h-2Z" />,
  complaints: <path d="M4 4h16v11H7l-3 3V4Zm7 2v5h2V6h-2Zm0 6v2h2v-2h-2Z" />,
};

const SECTIONS: { title: string; items: [label: string, href: string, icon: string][] }[] = [
  { title: '', items: [['Overview', '/control-7f3a9b2c', 'overview']] },
  { title: 'Queue', items: [['Verification queue', '/control-7f3a9b2c/verify', 'verify'], ['Appeals', '/control-7f3a9b2c/appeals', 'appeals']] },
  { title: 'Marketplace', items: [['Users', '/control-7f3a9b2c/users', 'users'], ['Listings & products', '/control-7f3a9b2c/listings', 'listings'], ['Orders & delivery', '/control-7f3a9b2c/orders', 'orders']] },
  { title: 'Money & trust', items: [['Escrow & disputes', '/control-7f3a9b2c/escrow', 'escrow'], ['Complaints', '/control-7f3a9b2c/complaints', 'complaints']] },
];

export function AdminLayout() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <div className="min-h-screen lg:flex bg-cream">
      {/* Mobile top bar */}
      <header className="section-dark lg:hidden sticky top-0 z-40 flex items-center gap-3 px-4 h-14 bg-ink text-cream border-b border-ink-600">
        <button onClick={() => setDrawerOpen(true)} aria-label="Open menu" className="w-9 h-9 grid place-items-center rounded-lg hover:bg-white/10 transition">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </button>
        <Link to="/" className="display text-gold text-xl leading-none">Souqli · Admin</Link>
      </header>

      {drawerOpen && <div className="lg:hidden fixed inset-0 z-40 bg-ink/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />}

      <aside
        className={`bg-ink text-cream border-r border-ink-600 transition-transform duration-300 ease-souq
          fixed inset-y-0 left-0 z-50 w-[248px] max-w-[85vw] ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shrink-0 lg:sticky lg:top-0 lg:h-screen lg:max-w-none flex flex-col`}
      >
        <button onClick={() => setDrawerOpen(false)} aria-label="Close menu" className="lg:hidden absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-lg text-mute hover:text-cream hover:bg-white/10">✕</button>

        <div className="px-6 py-6">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="w-9 h-9 rounded-[11px] bg-gold text-ink grid place-items-center display text-lg font-semibold">S</span>
            <span>
              <span className="block display text-gold text-lg leading-none">Souqli</span>
              <span className="block mono text-[9px] tracking-[0.28em] uppercase text-cream/45 mt-0.5">Admin console</span>
            </span>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
          {SECTIONS.map((sec, i) => (
            <div key={i}>
              {sec.title && <div className="px-3 mb-1.5 mono text-[9px] tracking-[0.24em] uppercase text-cream/35">{sec.title}</div>}
              <div className="space-y-0.5">
                {sec.items.map(([label, href, icon]) => (
                  <NavLink
                    key={href}
                    to={href}
                    end={href === '/control-7f3a9b2c'}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 py-2.5 rounded-luxe text-[13px] transition-all
                       ${isActive
                        ? 'bg-gold/15 text-gold-light font-medium'
                        : 'text-cream/60 hover:text-cream hover:bg-white/[0.06]'}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-gold" />}
                        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] shrink-0" fill="currentColor">{I[icon]}</svg>
                        <span className="truncate">{label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-6 py-4 border-t border-ink-600">
          <Link to="/" className="mono text-[10px] tracking-[0.14em] uppercase text-cream/40 hover:text-gold-light transition">← Back to marketplace</Link>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden"><Outlet /></main>
    </div>
  );
}
